import * as React from "react";
import * as moment from "moment";
import { Map, InfoWindow, Marker, GoogleApiWrapper, MarkerProps, MapProps } from "google-maps-react";

import { GeocodedHorseForSale, HorseForSale } from "src/model/HorseForSale";
import { ExtractFriendlyName } from "../../model/Customer";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faExternalLinkSquareAlt } from "@fortawesome/free-solid-svg-icons";

// Courtesy of https://medium.freecodecamp.org/how-to-change-javascript-google-map-marker-color-8a72131d1207
const MarkerUrl = (colour: string) => `http://maps.google.com/mapfiles/ms/icons/${colour}-dot.png`;

const GOOGLE_MAPS_COPY_ELEMENT_ID = "google-maps-copy-address";
const GOOGLE_MAPS_COPY_BUTTON_ID = "google-maps-button";
const GOOGLE_MAPS_COPY_BUTTON_ID_ICON = "google-maps-button-ICON";

interface Props {
    google: any;
    className?: string;
    data: Array<GeocodedHorseForSale>;
    markersDraggable: boolean;
    reload: boolean;
    onMarkerLocationChange?: (latitude: number, longitude: number) => void;
}

interface State {
    activeHorseForSale?: GeocodedHorseForSale;
    activeMarker?: google.maps.Marker;
}

type Coordinates = { lat: number; lng: number };

export class MapContainer extends React.Component<Props, State> {
    private address: HTMLTextAreaElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    handleMarkerLocation = (coord: any) => {
        const { latLng } = coord;
        const latitude = latLng.lat();
        const longitude = latLng.lng();
        this.props.onMarkerLocationChange!(latitude, longitude);
    };

    calculateBounds = () => {
        const bounds = new this.props.google.maps.LatLngBounds();
        this.props.data.forEach(horseForSale =>
            bounds.extend({
                lat: horseForSale.address911 && horseForSale.address911.latitude,
                lng: horseForSale.address911 && horseForSale.address911.longitude
            })
        );
        return bounds;
    };

    documentClicked = (e: MouseEvent) => {
        if (e && e.target && (e.target as any).id === GOOGLE_MAPS_COPY_BUTTON_ID) {
            e.preventDefault();
            this.copyToClipboard(e);
        }
    };

    copyToClipboard = (e: any) => {
        const textarea = document.getElementById(GOOGLE_MAPS_COPY_ELEMENT_ID);
        if (textarea !== null) {
            (textarea as HTMLTextAreaElement).select();
            document.execCommand("copy");
            e.target.focus();
        }
    };

    componentDidMount() {
        if (document.queryCommandSupported("copy")) {
            document.addEventListener("click", this.documentClicked);
        }
    }

    componentWillUnmount() {
        if (document.queryCommandSupported("copy")) {
            document.removeEventListener("click", this.documentClicked);
        }
    }

    render() {
        const bounds = this.calculateBounds();

        let currentInfoWindowAddress;
        if (this.state.activeHorseForSale && this.state.activeHorseForSale.address911) {
            currentInfoWindowAddress =
                (this.state.activeHorseForSale.address911.address ? this.state.activeHorseForSale.address911.address.trim() + ", " : "") +
                (this.state.activeHorseForSale.address911.city ? this.state.activeHorseForSale.address911.city.trim() + ", " : "") +
                (this.state.activeHorseForSale.address911.province ? this.state.activeHorseForSale.address911.province.abbreviation + ", " : "") +
                (this.state.activeHorseForSale.address911.postalCode ? this.state.activeHorseForSale.address911.postalCode.trim() + " " : " ") +
                (this.state.activeHorseForSale.address911.country ? this.state.activeHorseForSale.address911.country.abbreviation : "");
        }

        return (
            <Map
                setMyLocationEnable={false}
                centerAroundCurrentLocation={false}
                key={this.props.reload.toString()}
                google={this.props.google}
                initialCenter={{ lat: 42.9849, lng: -81.2453 }}
                onReady={(mapProps, map) => {
                    if (!map) {
                        return;
                    }
                    map.fitBounds(bounds);
                    map.panToBounds(bounds);
                    if (map.getZoom() > 8) {
                        map.setZoom(12);
                    }
                }}
            >
                {this.props.data.map((horseForSale, i) => {
                    const markerCoodinates = {
                        lat: (horseForSale.address911 && horseForSale.address911.latitude) || 0,
                        lng: (horseForSale.address911 && horseForSale.address911.longitude) || 0
                    };

                    return (
                        <Marker
                            title={horseForSale.sale.name}
                            key={horseForSale.id}
                            position={markerCoodinates}
                            draggable={this.props.markersDraggable}
                            onClick={(props, marker, e) => {
                                this.setState({ activeHorseForSale: horseForSale, activeMarker: marker });
                            }}
                            onDragend={(markerProps?: MarkerProps, map?: google.maps.Marker, event?: any) =>
                                 event && this.handleMarkerLocation(event)
                            }
                            icon={{
                                url: MarkerUrl(horseForSale.inspectionDate ? "green" : "red")
                            }}
                        />
                    );
                })}

                {/* Note that the `map` attribute of `InfoWindow` will need to be commented out in the definitions */}
                <InfoWindow
                    visible={!!(this.state.activeHorseForSale && this.state.activeMarker)}
                    marker={this.state.activeMarker as any}
                    google={this.props.google}
                >
                    <div className="info-window">
                        {this.state.activeHorseForSale && (
                            <>
                                <div className="title">
                                    <a href={`/#/horse/${this.state.activeHorseForSale.horse.id}`} target="_blank">
                                        {this.state.activeHorseForSale.horse.name}
                                        <FontAwesomeIcon icon={faExternalLinkSquareAlt} className="ml-1" size="xs" />
                                    </a>
                                    {/* <strong>{this.state.activeHorseForSale.horse.name}</strong> */}
                                </div>
                                {this.state.activeHorseForSale.contact && (
                                    <div className="contact">
                                        {`${ExtractFriendlyName(this.state.activeHorseForSale.contact)} 
                                        ${this.state.activeHorseForSale.contact.primaryPhone}`}
                                    </div>
                                )}
                                {this.state.activeHorseForSale.address911 && (
                                    <textarea
                                        readOnly={true}
                                        className="address"
                                        id={GOOGLE_MAPS_COPY_ELEMENT_ID}
                                        value={currentInfoWindowAddress}
                                        style={{ margin: 0 }}
                                    />
                                )}
                                {this.state.activeHorseForSale.inspectionDate ? (
                                    <span>
                                        Scheduled for{" "}
                                        <strong>{moment(this.state.activeHorseForSale.inspectionDate).format("YYYY-MM-DD hh:mm a")}</strong>
                                    </span>
                                ) : (
                                    <span>
                                        <strong>Unscheduled</strong>
                                    </span>
                                )}

                                {document.queryCommandSupported("copy") && this.state.activeHorseForSale.address911 && (
                                    <div className="info-window-footer">
                                        <button
                                            id={GOOGLE_MAPS_COPY_BUTTON_ID}
                                            type="button"
                                            title="Copy Content"
                                            className="btn btn-secondary btn-sm mx-0"
                                            style={{ fontSize: ".8rem", padding: ".2rem .5rem" }}
                                        >
                                            {" "}
                                            Copy Address
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </InfoWindow>
            </Map>
        );
    }
}

export default GoogleApiWrapper({
    apiKey: `${process.env.REACT_APP_GOOGLE_MAPS_KEY}`
})(MapContainer);



// WEBPACK FOOTER //
// ./src/components/common/GoogleMapsSale.tsx